import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, API } from "@/App";

const BrandingContext = createContext(null);

export function BrandingProvider({ children, isAuthenticated }) {
  const [mainLogo, setMainLogo] = useState(null);
  const [publicMainLogo, setPublicMainLogo] = useState(null);
  const [marketLogos, setMarketLogos] = useState({});
  const [loaded, setLoaded] = useState(false);

  const fetchBranding = useCallback(async () => {
    try {
      const res = await api.get("/branding");
      setMainLogo(res.data?.main_logo || null);
      setMarketLogos(res.data?.market_logos || {});
    } catch (e) {
      // Branding is optional — log but don't disrupt the app
      console.warn("Failed to fetch branding:", e?.message || e);
    } finally {
      setLoaded(true);
    }
  }, []);

  const fetchPublicBranding = useCallback(async () => {
    try {
      const res = await api.get("/branding/public");
      setPublicMainLogo(res.data?.main_logo || null);
    } catch (e) {
      console.warn("Failed to fetch public branding:", e?.message || e);
    }
  }, []);

  useEffect(() => {
    // Public main logo is always available (login page needs it)
    fetchPublicBranding();
    if (isAuthenticated) {
      fetchBranding();
    } else {
      setMainLogo(null);
      setMarketLogos({});
      setLoaded(true);
    }
  }, [isAuthenticated, fetchBranding, fetchPublicBranding]);

  const base = API.replace(/\/api$/, "");

  const withAuth = useCallback(
    (path) => {
      if (!path) return null;
      const token = localStorage.getItem("efacilicare_token");
      if (!token) return null;
      const sep = path.includes("?") ? "&" : "?";
      return `${base}${path}${sep}auth=${encodeURIComponent(token)}`;
    },
    [base]
  );

  // For login page (no auth) — uses /api/branding/public/files
  const getMainLogoUrl = useCallback(() => {
    if (isAuthenticated && mainLogo) return withAuth(mainLogo);
    if (publicMainLogo) return `${base}${publicMainLogo}`;
    return null;
  }, [isAuthenticated, mainLogo, publicMainLogo, withAuth, base]);

  const getMarketLogoUrl = useCallback(
    (marketId) => {
      if (!marketId) return null;
      const path = marketLogos[marketId] || marketLogos[marketId?.toLowerCase()];
      return withAuth(path);
    },
    [marketLogos, withAuth]
  );

  return (
    <BrandingContext.Provider
      value={{
        mainLogoPath: mainLogo || publicMainLogo,
        marketLogosMap: marketLogos,
        getMainLogoUrl,
        getMarketLogoUrl,
        refreshBranding: async () => {
          await fetchPublicBranding();
          if (isAuthenticated) await fetchBranding();
        },
        loaded,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    return {
      mainLogoPath: null,
      marketLogosMap: {},
      getMainLogoUrl: () => null,
      getMarketLogoUrl: () => null,
      refreshBranding: () => {},
      loaded: false,
    };
  }
  return ctx;
}
